import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import {
  BackendUpdateResponse,
  Datastore,
  DatastoreCollection,
  DatastoreDocument,
} from '@freelancer/datastore';
import {
  Currency,
  EmployerReputation,
  FreelancerReputationWithRehire,
  LocalHourlyRate,
  ProfileViewUser,
  ProfileViewUsersCollection,
  ReviewsCollection,
  UsersSelfCollection,
} from '@freelancer/datastore/collections';
import { EmailVerification } from '@freelancer/email-verification';
import { BannerAlertType } from '@freelancer/ui/banner-alert';
import { ButtonColor } from '@freelancer/ui/button';
import { CardSize } from '@freelancer/ui/card';
import { IconSize } from '@freelancer/ui/icon';
import { InputType } from '@freelancer/ui/input';
import { Margin } from '@freelancer/ui/margin';
import { FontWeight } from '@freelancer/ui/text';
import { ToastAlertService, ToastAlertType } from '@freelancer/ui/toast-alert';
import { AvatarSize } from '@freelancer/ui/user-avatar';
import {
  dirtyAndValidate,
  maxLength,
  maxValue,
  minLength,
  minValue,
  required,
  wholeNumber,
} from '@freelancer/ui/validators';
import { isDefined, isFormControl, toNumber } from '@freelancer/utils';
import { RoleApi } from 'api-typings/common/common';
import { ErrorCodeApi } from 'api-typings/errors/errors';
import {
  HOURLY_RATE_MAXApi,
  HOURLY_RATE_MINApi,
  PROFILE_DESCRIPTION_MAX_LENGTHApi,
  PROFILE_DESCRIPTION_MIN_LENGTHApi,
  UserChosenRoleApi,
} from 'api-typings/users/users';
import * as Rx from 'rxjs';
import {
  distinctUntilChanged,
  filter,
  map,
  pairwise,
  take,
} from 'rxjs/operators';
import { EmailVerificationBannerToastItem } from '../../email-verification-banner/email-verification-banner-alert.component';
import { UserProfileViewState } from '../user-profile-editable-ui/user-profile-editable-ui.model';
import { UserReputation } from './user-profile-summary-reputation/user-profile-summary-reputation.component';

@Component({
  selector: 'app-user-profile-summary',
  template: `
    <fl-card
      class="UserProfileSummary"
      [size]="CardSize.SMALL"
      [flTrackingSection]="'UserProfileSummary'"
    >
      <fl-grid
        class="UserSummary"
        *ngIf="user$ | flAsync as user; else loading"
      >
        <!-- Email verification successful toast -->
        <fl-toast-alert
          i18n="Email has been verified toast alert"
          [id]="EmailVerificationBannerToastItem.VERIFY_SUCCESS"
          [type]="ToastAlertType.SUCCESS"
          [timeout]="5000"
        >
          Email successfully verified.
        </fl-toast-alert>

        <fl-col [col]="12" [colTablet]="4" [colDesktopSmall]="4">
          <fl-grid>
            <fl-col class="UserProfileColumn" [flMarginBottom]="Margin.XSMALL">
              <!-- Counter-balance -->
              <fl-bit class="UserProfileImage">
                <app-user-profile-picture
                  [isEditable]="showProfileImageEditButton$ | flAsync"
                  [avatar]="user.avatarLarge"
                  [temporarySavedCroppedImage]="
                    temporarySavedCroppedImage$ | flAsync
                  "
                  (saveTemporaryCroppedImage)="
                    handleSaveTemporaryCroppedImage($event)
                  "
                >
                </app-user-profile-picture>
              </fl-bit>
            </fl-col>

            <ng-container *ngIf="isViewMode$ | flAsync; else editHourlyRate">
              <fl-col
                *ngIf="showOnlineIndicator$ | flAsync"
                [flMarginBottom]="Margin.XSMALL"
                [flHideMobile]="true"
              >
                <app-user-profile-summary-online-indicator
                  [isOnline$]="isOnline$"
                ></app-user-profile-summary-online-indicator>
              </fl-col>

              <fl-col [flHideMobile]="true">
                <app-user-profile-summary-information
                  [isAgent]="isAgent$ | flAsync"
                  [isCorporate]="isCorporate$ | flAsync"
                  [isLoggedIn$]="isLoggedIn$"
                  [isOnline$]="isOnline$"
                  [localHourlyRate$]="localHourlyRate$"
                  [localHourlyRateLoading$]="localHourlyRateLoading$"
                  [location]="user.location"
                  [responsiveness]="user.responsiveness"
                  [isDeloitteDcUser]="user.isDeloitteDcUser"
                  [status]="user.status"
                ></app-user-profile-summary-information>
              </fl-col>

              <fl-col [flHideMobile]="true">
                <app-user-profile-summary-extra-info
                  [registrationDate]="user.registrationDate"
                  [recommendations]="user.recommendations"
                  [timezone]="user.timezone"
                  [userIsAgent]="isAgent$ | flAsync"
                  [isLoggedIn]="isLoggedIn$ | flAsync"
                >
                </app-user-profile-summary-extra-info>
              </fl-col>
            </ng-container>

            <ng-template #editHourlyRate>
              <fl-col
                *ngIf="!(isDeloitteUserProfile$ | flAsync)"
                [flMarginBottom]="Margin.MID"
              >
                <fl-label
                  class="HourlyRateLabel"
                  i18n="Hourly Rate label"
                  [flMarginBottom]="Margin.XXSMALL"
                  [for]="'hourlyRateEdit'"
                  [weight]="FontWeight.BOLD"
                >
                  Hourly Rate
                </fl-label>
                <ng-container
                  *ngIf="
                    localHourlyRate$ | flAsync as localHourlyRate;
                    else defaultHourlyRate
                  "
                >
                  <ng-container *ngIf="formGroup.get('hourlyRate') as control">
                    <fl-input
                      *ngIf="isFormControl(control)"
                      flTrackingLabel="hourlyRateFocus"
                      [id]="'hourlyRateEdit'"
                      [disabled]="
                        isSavingSummary || (showEmailVerifyBanner$ | flAsync)
                      "
                      [beforeLabel]="localHourlyRate.currency.sign"
                      afterLabel="{{ localHourlyRate.currency.code }} / Hr"
                      i18n-afterLabel="
                        User profile hourly rate currency label representing
                        units (eg. AUD / Hr)
                      "
                      [control]="control"
                      [type]="InputType.NUMBER"
                    >
                    </fl-input>
                  </ng-container>
                </ng-container>
                <ng-template #defaultHourlyRate>
                  <ng-container *ngIf="formGroup.get('hourlyRate') as control">
                    <fl-input
                      *ngIf="isFormControl(control)"
                      flTrackingLabel="hourlyRateFocus"
                      [id]="'hourlyRateEdit'"
                      [disabled]="
                        isSavingSummary || (showEmailVerifyBanner$ | flAsync)
                      "
                      [beforeLabel]="defaultCurrency.sign"
                      afterLabel="{{ defaultCurrency.code }} / Hr"
                      i18n-afterLabel="
                        User profile hourly rate currency label representing
                        units (eg. AUD / Hr)
                      "
                      [control]="control"
                      [type]="InputType.NUMBER"
                    >
                    </fl-input>
                  </ng-container>
                </ng-template>
              </fl-col>
            </ng-template>
          </fl-grid>
        </fl-col>

        <fl-col [col]="12" [colTablet]="8" [colDesktopSmall]="8">
          <fl-grid>
            <fl-col class="SummaryHeader">
              <app-user-profile-summary-name
                [flHideMobile]="!(isViewMode$ | flAsync)"
                [profileUserId]="user.id"
                [isCorporate]="isCorporate$ | flAsync"
                [username]="user.username"
                [publicName]="user.publicName"
                [isPreferredFreelancer]="user.preferredFreelancer"
                [isFreelancerVerified]="user.status.freelancerVerifiedUser"
                [isLoggedIn]="isLoggedIn$ | flAsync"
                [isViewerProfileOwner]="isViewerProfileOwner$ | flAsync"
                [enterpriseIds]="user.enterpriseIds"
              >
              </app-user-profile-summary-name>
              <fl-button
                *ngIf="showEditButton$ | flAsync"
                class="EditButton"
                flTrackingLabel="EditProfileButtonClick"
                i18n="Edit Profile Button"
                [color]="ButtonColor.SECONDARY"
                [flHideMobile]="true"
                [flMarginBottom]="Margin.SMALL"
                (click)="editingSummarySwitch(true)"
              >
                Edit Profile
              </fl-button>
            </fl-col>

            <ng-container *ngIf="isViewMode$ | flAsync; else editUserSummary">
              <fl-col
                *ngIf="!(isAgent$ | flAsync)"
                [flMarginBottom]="Margin.XXSMALL"
                [flMarginBottomTablet]="Margin.XSMALL"
                [colDesktopSmallOrder]="2"
              >
                <app-user-profile-summary-rating
                  [reputation]="userReputation$ | flAsync"
                  [isDeloitteDcUser]="user.isDeloitteDcUser"
                >
                </app-user-profile-summary-rating>
              </fl-col>

              <fl-col [flShowMobile]="true">
                <app-user-profile-summary-information
                  [isLoggedIn$]="isLoggedIn$"
                  [localHourlyRate$]="localHourlyRate$"
                  [localHourlyRateLoading$]="localHourlyRateLoading$"
                  [location]="user.location"
                  [status]="user.status"
                ></app-user-profile-summary-information>
              </fl-col>

              <fl-col *ngIf="!(isAgent$ | flAsync)" [colDesktopSmallOrder]="3">
                <app-user-profile-summary-reputation
                  [isDeloitteUserProfile$]="isDeloitteUserProfile$"
                  [userReputation$]="userRoleReputation$"
                ></app-user-profile-summary-reputation>
              </fl-col>

              <fl-col
                [flMarginBottom]="Margin.XSMALL"
                [colDesktopSmallOrder]="1"
              >
                <app-user-profile-summary-tagline
                  [tagline]="user.tagLine"
                ></app-user-profile-summary-tagline>
              </fl-col>

              <fl-col
                *ngIf="isCorporate$ | flAsync"
                [colDesktopSmallOrder]="3"
                [flMarginBottom]="Margin.XSMALL"
              >
                <app-user-profile-summary-featured-review
                  [featuredReviewsCollection]="featuredReviewsCollection"
                  [isViewerProfileOwner]="isViewerProfileOwner$ | flAsync"
                  (featuredReviewClick)="featuredReviewClick.emit()"
                ></app-user-profile-summary-featured-review>
              </fl-col>

              <fl-col
                [flMarginBottom]="Margin.XSMALL"
                [colDesktopSmallOrder]="3"
              >
                <app-user-profile-summary-description
                  [description]="user.profileDescription"
                  [isViewerProfileOwner]="isViewerProfileOwner$ | flAsync"
                  [isLoggedIn]="isLoggedIn$ | flAsync"
                ></app-user-profile-summary-description>
              </fl-col>
            </ng-container>

            <ng-template #editUserSummary>
              <fl-col [flMarginBottom]="Margin.SMALL">
                <!-- Email verification wall banner -->
                <app-email-verification-banner-alert
                  *ngIf="
                    (showEmailVerifyBanner$ | flAsync) &&
                    (userEmail$ | flAsync) as userEmail
                  "
                  i18n="Email verification wall banner"
                >
                  Please click on the link in the verification email we sent to
                  {{ userEmail }} to activate your account and continue posting
                  your project.
                </app-email-verification-banner-alert>
              </fl-col>
              <fl-col [flMarginBottom]="Margin.SMALL">
                <fl-label
                  class="Label"
                  i18n="Professional Headline label"
                  [flMarginBottom]="Margin.XXSMALL"
                  [for]="'professionalHeadlineEdit'"
                  [weight]="FontWeight.BOLD"
                >
                  Professional Headline
                </fl-label>
                <ng-container
                  *ngIf="formGroup.get('professionalHeadline') as control"
                >
                  <fl-textarea
                    *ngIf="isFormControl(control)"
                    flTrackingLabel="ProfessionalHeadlineFocus"
                    [id]="'professionalHeadlineEdit'"
                    [disabled]="
                      isSavingSummary || (showEmailVerifyBanner$ | flAsync)
                    "
                    [rows]="1"
                    [control]="control"
                  ></fl-textarea>
                </ng-container>
              </fl-col>
              <fl-col [flMarginBottom]="Margin.SMALL">
                <fl-label
                  class="Label"
                  i18n="User Edit Summary label"
                  [flMarginBottom]="Margin.XXSMALL"
                  [for]="'userSummaryEdit'"
                  [weight]="FontWeight.BOLD"
                >
                  Summary
                </fl-label>
                <ng-container *ngIf="formGroup.get('summary') as control">
                  <fl-textarea
                    *ngIf="isFormControl(control)"
                    flTrackingLabel="SummaryFocus"
                    [flMarginBottom]="Margin.SMALL"
                    [id]="'userSummaryEdit'"
                    [disabled]="
                      isSavingSummary || (showEmailVerifyBanner$ | flAsync)
                    "
                    [rows]="8"
                    [control]="control"
                  ></fl-textarea>
                </ng-container>
              </fl-col>
              <fl-col
                class="BottomButtons"
                [flHideMobile]="true"
                [flMarginBottom]="Margin.SMALL"
              >
                <fl-button
                  i18n="Cancel User Summary Edit"
                  flTrackingLabel="CancelButtonClick"
                  [flMarginRight]="Margin.SMALL"
                  [disabled]="isSavingSummary"
                  [color]="ButtonColor.DEFAULT"
                  (click)="handleCancelClicked()"
                >
                  Cancel
                </fl-button>
                <fl-button
                  i18n="Save User Summary"
                  flTrackingLabel="SaveButtonClick"
                  [busy]="isSavingSummary"
                  [disabled]="showEmailVerifyBanner$ | flAsync"
                  [color]="ButtonColor.SECONDARY"
                  (click)="handleSaveClicked()"
                >
                  Save
                </fl-button>
              </fl-col>
              <ng-container *ngIf="savingError | flAsync as response">
                <fl-col
                  *ngIf="response.status !== 'success'"
                  [ngSwitch]="response.errorCode"
                >
                  <fl-banner-alert
                    *ngSwitchCase="ErrorCodeApi.FORBIDDEN"
                    i18n="User Profile Summary Email Verify Required Error"
                    [type]="BannerAlertType.ERROR"
                    [flMarginBottom]="Margin.SMALL"
                  >
                    <fl-text [flMarginBottom]="Margin.XXSMALL">
                      You need to
                      <fl-link
                        flTrackingLabel="RedirectToEmailVerifyPage"
                        [link]="'/users/verify.php'"
                        [queryParams]="{ id: user.id, sellerId: user.id }"
                        (click)="sendVerificationEmail()"
                      >
                        verify your email
                      </fl-link>
                      to continue editing your profile.
                    </fl-text>
                  </fl-banner-alert>

                  <fl-banner-alert
                    *ngSwitchDefault
                    [type]="BannerAlertType.ERROR"
                    [flMarginBottom]="Margin.SMALL"
                  >
                    <fl-generic-error [error]="response"></fl-generic-error>
                  </fl-banner-alert>
                </fl-col>
              </ng-container>
            </ng-template>
          </fl-grid>
        </fl-col>
      </fl-grid>

      <fl-bit
        *ngIf="showMyListsButton$ | flAsync"
        class="MyListsButtonContainer"
      >
        <app-user-list-select-button
          [selectedUser]="user$ | flAsync"
          [sizeDesktop]="IconSize.LARGE"
        ></app-user-list-select-button>
      </fl-bit>

      <ng-template #loading>
        <fl-grid class="UserSummary">
          <fl-col [col]="12" [colTablet]="4" [colDesktopSmall]="4">
            <fl-bit [flMarginBottom]="Margin.XSMALL">
              <fl-loading-text [height]="50"></fl-loading-text>
            </fl-bit>

            <fl-bit [flHideMobile]="true">
              <fl-loading-text [padded]="false" [rows]="3"></fl-loading-text>
            </fl-bit>
          </fl-col>

          <fl-col [col]="12" [colTablet]="8" [colDesktopSmall]="8">
            <fl-bit [flMarginBottom]="Margin.MID">
              <fl-loading-text [padded]="false" [rows]="1"></fl-loading-text>
            </fl-bit>

            <fl-bit [flMarginBottom]="Margin.MID">
              <fl-loading-text [padded]="false" [rows]="2"></fl-loading-text>
            </fl-bit>

            <fl-bit [flMarginBottom]="Margin.MID">
              <fl-loading-text [padded]="false" [rows]="2"></fl-loading-text>
            </fl-bit>

            <fl-bit [flMarginBottom]="Margin.MID">
              <fl-loading-text [padded]="false" [rows]="3"></fl-loading-text>
            </fl-bit>
          </fl-col>
        </fl-grid>
      </ng-template>
    </fl-card>
  `,
  styleUrls: ['./user-profile-summary.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class UserProfileSummaryComponent implements OnInit, OnDestroy {
  AvatarSize = AvatarSize;
  BannerAlertType = BannerAlertType;
  ButtonColor = ButtonColor;
  CardSize = CardSize;
  EmailVerificationBannerToastItem = EmailVerificationBannerToastItem;
  ErrorCodeApi = ErrorCodeApi;
  FontWeight = FontWeight;
  IconSize = IconSize;
  InputType = InputType;
  isFormControl = isFormControl;
  Margin = Margin;
  RoleApi = RoleApi;
  ToastAlertType = ToastAlertType;

  @Input() defaultCurrency: Currency;
  @Input() featuredReviewsCollection: DatastoreCollection<ReviewsCollection>;
  @Input() isAgent$: Rx.Observable<boolean>;
  @Input() isCorporate$: Rx.Observable<boolean>;
  @Input() isDeloitteUserProfile$: Rx.Observable<boolean>;
  @Input() isLoggedIn$: Rx.Observable<boolean>;
  @Input() isOnline$: Rx.Observable<boolean>;
  @Input() isViewerProfileOwner$: Rx.Observable<boolean>;
  @Input() localHourlyRate$: Rx.Observable<LocalHourlyRate | undefined>;
  @Input() localHourlyRateLoading$: Rx.Observable<boolean>;
  @Input() profileUserId$: Rx.Observable<number>;
  @Input() triggerCancelChanges$: Rx.Observable<void>;
  @Input() triggerSaveChanges$: Rx.Observable<void>;
  @Input() userDocument: DatastoreDocument<ProfileViewUsersCollection>;
  @Input() userReputation$: Rx.Observable<
    FreelancerReputationWithRehire | EmployerReputation
  >;
  @Input() viewProfileAs$: Rx.Observable<RoleApi>;
  @Input() viewState$: Rx.Observable<UserProfileViewState>;

  @Output() featuredReviewClick = new EventEmitter();
  @Output() toggleViewMode = new EventEmitter<UserProfileViewState>();
  @Output() savingSummary = new EventEmitter<boolean>();

  private _temporarySavedCroppedImage$ = new Rx.Subject<string | undefined>();
  temporarySavedCroppedImage$ = this._temporarySavedCroppedImage$.asObservable();

  cancelChangesSubscription?: Rx.Subscription;
  readonly PROFILE_TAGLINE_MAX_LENGTH = 50;

  formInitSubscription?: Rx.Subscription;
  isSavingSummary: boolean;
  isViewMode$: Rx.Observable<boolean>;
  saveChangesSubscription?: Rx.Subscription;
  savingError: Promise<BackendUpdateResponse<ProfileViewUsersCollection>>;
  sendVerificationEmailSubscription?: Rx.Subscription;
  showEditButton$: Rx.Observable<boolean>;
  showEmailVerifyBanner$: Rx.Observable<boolean>;
  showMyListsButton$: Rx.Observable<boolean>;
  showOnlineIndicator$: Rx.Observable<boolean>;
  showProfileImageEditButton$: Rx.Observable<boolean>;
  user$: Rx.Observable<ProfileViewUser>;
  userEmail$: Rx.Observable<string>;
  userRoleReputation$: Rx.Observable<UserReputation>;

  professionalHeadlineControl = new FormControl('', [
    required($localize`Please enter your professional headline.`),
    minLength(10, $localize`Please enter at least 10 characters.`),
    maxLength(
      this.PROFILE_TAGLINE_MAX_LENGTH,
      $localize`Please enter at most ${this.PROFILE_TAGLINE_MAX_LENGTH} characters.`,
    ),
  ]);

  summaryControl = new FormControl('', [
    required($localize`Please enter a profile summary.`),
    minLength(
      PROFILE_DESCRIPTION_MIN_LENGTHApi,
      $localize`Please enter at least ${PROFILE_DESCRIPTION_MIN_LENGTHApi} characters.`,
    ),
    maxLength(
      PROFILE_DESCRIPTION_MAX_LENGTHApi,
      $localize`Please enter at most ${PROFILE_DESCRIPTION_MAX_LENGTHApi} characters.`,
    ),
  ]);

  hourlyControl = new FormControl('', [
    required($localize`Please enter your hourly rate.`),
    wholeNumber($localize`Please enter an hourly rate as a whole number.`),
    minValue(
      HOURLY_RATE_MINApi,
      $localize`Please enter an hourly rate of at least ${HOURLY_RATE_MINApi}.`,
    ),
    maxValue(
      HOURLY_RATE_MAXApi,
      $localize`Please enter an hourly rate no more than ${HOURLY_RATE_MAXApi}.`,
    ),
  ]);

  formGroup: FormGroup = new FormGroup({
    professionalHeadline: this.professionalHeadlineControl,
    summary: this.summaryControl,
    hourlyRate: this.hourlyControl,
  });

  initialFormValues: {
    professionalHeadline: string;
    summary: string;
    hourlyRate?: number | string;
  };

  constructor(
    private changeDetectorRef: ChangeDetectorRef,
    private datastore: Datastore,
    private emailVerify: EmailVerification,
    private toastAlertService: ToastAlertService,
  ) {}

  ngOnInit() {
    this.user$ = this.userDocument.valueChanges();

    this.userEmail$ = this.datastore
      .document<UsersSelfCollection>('usersSelf', this.profileUserId$)
      .valueChanges()
      .pipe(
        map(user => user.email),
        filter(isDefined),
      );

    this.showEditButton$ = Rx.combineLatest([
      this.isViewerProfileOwner$,
      this.viewState$,
    ]).pipe(
      map(
        ([userCanEdit, viewState]) =>
          userCanEdit && viewState === UserProfileViewState.VIEW,
      ),
    );

    this.isViewMode$ = this.viewState$.pipe(
      map(state => state === UserProfileViewState.VIEW),
    );

    const isEmployer$ = this.user$.pipe(
      map(user => user.chosenRole === UserChosenRoleApi.EMPLOYER),
    );

    const isEnterpriseUser$ = this.user$.pipe(
      map(
        user => isDefined(user.enterpriseIds) && user.enterpriseIds.length > 0,
      ),
    );

    const isUserEmailVerified$ = this.user$.pipe(
      map(user => (user.status ? user.status.emailVerified : false)),
      distinctUntilChanged(),
    );

    const isNewlyEmailVerified$ = isUserEmailVerified$.pipe(
      pairwise(),
      map(
        ([previouslyEmailVerified, currentlyEmailVerified]) =>
          !previouslyEmailVerified && currentlyEmailVerified,
      ),
    );

    // Show the email verification banner when:
    // - the user is not an employer,
    // - the user is not an enterprise user,
    // - the user is not email verified,
    // - and the user is not in Profile view mode (they should be in Edit Profile view mode).
    this.showEmailVerifyBanner$ = Rx.combineLatest([
      isEmployer$,
      isEnterpriseUser$,
      isUserEmailVerified$,
      this.isViewMode$,
    ]).pipe(
      map(
        ([isEmployer, isEnterpriseUser, isUserEmailVerified, isViewMode]) =>
          !(
            isEmployer ||
            isEnterpriseUser ||
            isUserEmailVerified ||
            isViewMode
          ),
      ),
      distinctUntilChanged(),
    );

    // Send a verification email every time the email verification banner is shown
    this.sendVerificationEmailSubscription = this.showEmailVerifyBanner$
      .pipe(filter(showEmailVerifyBanner => showEmailVerifyBanner))
      .subscribe(() => {
        this.emailVerify.send();
      });

    const isNewlyHiddenEmailVerifyBanner$ = this.showEmailVerifyBanner$.pipe(
      pairwise(),
      map(
        ([previouslyShownBanner, currentlyShownBanner]) =>
          previouslyShownBanner && !currentlyShownBanner,
      ),
    );

    // Show the toast alert when:
    // - the user's email verification state has changed from not email verified to email verified,
    // - the email verification banner state has changed from shown to hidden,
    // - the user is not an employer,
    // - the user is not an enterprise user,
    // - and the user is not in Profile view mode (they should be in Edit Profile view mode)
    Rx.combineLatest([
      isNewlyEmailVerified$,
      isNewlyHiddenEmailVerifyBanner$,
      isEmployer$,
      isEnterpriseUser$,
      this.isViewMode$,
    ])
      .pipe(
        filter(
          ([
            isNewlyEmailVerified,
            isNewlyHiddenEmailVerifyBanner,
            isEmployer,
            isEnterpriseUser,
            isViewMode,
          ]) =>
            isNewlyEmailVerified &&
            isNewlyHiddenEmailVerifyBanner &&
            !isEmployer &&
            !isEnterpriseUser &&
            !isViewMode,
        ),
        take(1),
      )
      .toPromise()
      .then(() => {
        this.toastAlertService.open(
          EmailVerificationBannerToastItem.VERIFY_SUCCESS,
        );
      });

    this.showMyListsButton$ = Rx.combineLatest([
      this.isLoggedIn$,
      this.isViewerProfileOwner$,
    ]).pipe(
      map(
        ([isLoggedIn, isViewerProfileOwner]) =>
          isLoggedIn && !isViewerProfileOwner,
      ),
    );

    this.showProfileImageEditButton$ = Rx.combineLatest([
      this.isViewerProfileOwner$,
      this.showEditButton$,
    ]).pipe(
      map(([userCanEdit, showEditButton]) => userCanEdit && !showEditButton),
    );

    this.showOnlineIndicator$ = Rx.combineLatest([
      this.isCorporate$,
      this.isLoggedIn$,
    ]).pipe(map(([isCorporate, isLoggedIn]) => !isCorporate && isLoggedIn));

    this.formInitSubscription = Rx.combineLatest([
      this.user$,
      this.isDeloitteUserProfile$,
    ]).subscribe(([user, isDeloitteUserProfile]) => {
      if (isDeloitteUserProfile) {
        this.formGroup.removeControl('hourlyRate');
      }

      this.initialFormValues = {
        professionalHeadline: user.tagLine || '',
        summary: user.profileDescription || '',
        ...(!isDeloitteUserProfile
          ? { hourlyRate: user.hourlyRate || '' }
          : {}),
      };

      this.formGroup.setValue(this.initialFormValues);
    });

    this.cancelChangesSubscription = this.triggerCancelChanges$.subscribe(() =>
      this.handleCancelClicked(),
    );

    this.saveChangesSubscription = this.triggerSaveChanges$.subscribe(() =>
      this.handleSaveClicked(),
    );

    this.userRoleReputation$ = Rx.combineLatest([
      this.userReputation$,
      this.viewProfileAs$,
    ]).pipe(
      map(([userReputation, viewProfileAs]) => ({
        type: viewProfileAs,
        reputation: userReputation,
      })),
    );
  }

  editingSummarySwitch(editMode: boolean) {
    this.toggleViewMode.emit(
      editMode ? UserProfileViewState.EDIT : UserProfileViewState.VIEW,
    );
  }

  handleSaveClicked(): void {
    this.setSavingSummary(true);

    if (this.formGroup.invalid) {
      this.setSavingSummary(false);
      dirtyAndValidate(this.formGroup);
      return;
    }

    this.isDeloitteUserProfile$
      .pipe(take(1))
      .toPromise()
      .then(isDeloitteProfile => {
        // We only want to allow the user to press save if they have made
        // changes to the form, not just if the form is dirty.
        const sameTagLine =
          this.professionalHeadlineControl.value ===
          this.initialFormValues.professionalHeadline;

        const sameDescription =
          this.summaryControl.value === this.initialFormValues.summary;

        const sameHourlyRate = isDeloitteProfile
          ? true
          : toNumber(this.hourlyControl.value) ===
            toNumber(this.initialFormValues.hourlyRate);

        if (sameTagLine && sameDescription && sameHourlyRate) {
          this.setSavingSummary(false);
          this.editingSummarySwitch(false);
          return;
        }

        this.savingError = this.userDocument.update({
          profileDescription: this.summaryControl.value,
          tagLine: this.professionalHeadlineControl.value,
          hourlyRate: toNumber(this.hourlyControl.value),
        });

        this.savingError.then(response => {
          this.setSavingSummary(false);

          if (response.status === 'success') {
            this.editingSummarySwitch(false);
          }
        });
      });
  }

  handleSaveTemporaryCroppedImage(imageBase64: string | undefined) {
    this._temporarySavedCroppedImage$.next(imageBase64);
    this.changeDetectorRef.detectChanges();
  }

  handleCancelClicked() {
    this._temporarySavedCroppedImage$.next(undefined);
    this.editingSummarySwitch(false);
    this.formGroup.reset(this.initialFormValues);
  }

  setSavingSummary(status: boolean): void {
    this.isSavingSummary = status;
    this.savingSummary.emit(status);
  }

  sendVerificationEmail() {
    this.emailVerify.send();
  }

  ngOnDestroy() {
    if (this.formInitSubscription) {
      this.formInitSubscription.unsubscribe();
    }

    if (this.cancelChangesSubscription) {
      this.cancelChangesSubscription.unsubscribe();
    }

    if (this.saveChangesSubscription) {
      this.saveChangesSubscription.unsubscribe();
    }

    if (this.sendVerificationEmailSubscription) {
      this.sendVerificationEmailSubscription.unsubscribe();
    }
  }
}
